// Network · Events (2.22-2.29; migration 20261104090000, engine/events.py): the shapes the Events page reads
// (crm_events, crm_event_detail, crm_event_results), the business's reach from its settings (network.events), and
// small pure helpers. A person chooses every event and approves every pitch unless the settings say otherwise.
import type { JsonObject } from "./types";

export type EventStatus = "found" | "shortlisted" | "chosen" | "pitched" | "confirmed" | "attended" | "skipped" | "declined";
export type EventRole = "attend" | "exhibit" | "speak" | "sponsor" | "award";
export type EventChoice = EventRole | "skip";
export type PitchKind = "speaker" | "award" | "exhibitor" | "sponsor";
export type DeadlineKind = "speaking" | "award" | "exhibitor" | "sponsor" | "booking" | "other";

export interface EventRow {
  id: string;
  name: string;
  organiser: string | null;
  organiser_contact: string | null;
  organiser_contact_url: string | null;
  url: string | null;
  starts_on: string;
  ends_on: string | null;
  place: string | null;
  country: string | null;
  online: boolean;
  distance_km: number | null;
  expected_size: number | null;
  cost: number | null;
  currency: string | null;
  deadline: string | null;
  deadline_kind: DeadlineKind | null;
  roles: EventRole[];
  our_role: EventRole | null;
  fit_score: number | null;
  fit_reasons: string[];
  source: "register" | "web_search" | "inbox" | "owner" | "agent";
  source_detail: string | null;
  status: EventStatus;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  cost_actual: number | null;
  created_at: string;
  cta: { id: string; code: string; label: string } | null;
  listed: number;
  contacts: number;
  pitch: { id: string; status: string; kind: PitchKind | null; sent_at: string | null } | null;
  choose_task_id: string | null;
  /** 2.24 What the owner's calendar says about the event's dates (migration 20261108090000); null until checked. */
  calendar_check?: CalendarCheck | null;
}

/** The scheduler agent's free/busy check of an event's dates (crm_record_event_calendar_check). */
export interface CalendarCheck {
  status: "free" | "clash" | "not_connected" | "error";
  clashes?: { start: string; end: string }[];
  busy_minutes?: number;
  from?: string;
  to?: string;
  note?: string;
  checked_at?: string;
  checked_by?: string;
}

export interface EventAction {
  id: string;
  kind: "pitch" | "pitch_follow_up" | "meeting_target" | "contact";
  step_id: string;
  status: string;
  pitch_kind?: PitchKind;
  subject?: string;
  body?: string;
  to_email?: string;
  person_id?: string;
  person_name?: string;
  organisation_id?: string;
  organisation_name?: string;
  detail?: JsonObject;
  note?: string;
  task_id?: string;
  decided_by?: string;
  decided_at?: string;
  sent_at?: string;
  created_at: string;
}

export interface EventDetail extends Omit<EventRow, "cta" | "listed" | "contacts" | "pitch" | "choose_task_id"> {
  actions: EventAction[];
  cta: { id: string; code: string; label: string; destination: string | null } | null;
}

export interface EventResult {
  event_id: string;
  name: string;
  starts_on: string;
  status: EventStatus;
  our_role: EventRole | null;
  cost: number | null;
  cost_is_actual: boolean;
  currency: string | null;
  contacts: number;
  listed: number;
  followed_up: number;
  meetings: number;
  held: number;
  deals: number;
  won: number;
  won_value: number;
  qr_enquiries: number;
  cost_per_meeting: number | null;
}

export interface NewEvent {
  name: string;
  starts_on: string;
  ends_on?: string;
  url?: string;
  place?: string;
  country?: string;
  online?: boolean;
  organiser?: string;
  organiser_contact?: string;
  cost?: number;
  deadline?: string;
  deadline_kind?: DeadlineKind;
  roles?: EventRole[];
}

export interface NewContact {
  full_name?: string;
  email?: string;
  phone?: string;
  role?: string;
  company?: string;
  note?: string;
}

/** network.events, as each business sets it (decided 10 Oct 2026: reach is the business's own; approvals on by default). */
export interface EventSettings {
  enabled: boolean;
  keywords: string[];
  markets: string[];
  max_travel_km: number;
  base: string;
  horizon_months: number;
  max_cost: number;
  roles: EventRole[];
  follow_up_days: number;
  approve_events: boolean;
  approve_pitches: boolean;
}

export const ROLES: EventRole[] = ["attend", "exhibit", "speak", "sponsor", "award"];

export const ROLE_LABEL: Record<EventRole, string> = {
  attend: "Attend",
  exhibit: "Exhibit",
  speak: "Speak",
  sponsor: "Sponsor",
  award: "Enter an award",
};

export const STATUS_LABEL: Record<EventStatus, string> = {
  found: "Found",
  shortlisted: "Shortlisted",
  chosen: "Chosen",
  pitched: "Pitched",
  confirmed: "Confirmed",
  attended: "Attended",
  skipped: "Skipped",
  declined: "Declined",
};

export const STATUS_TONE: Record<EventStatus, string> = {
  found: "plain",
  shortlisted: "warn",
  chosen: "info",
  pitched: "info",
  confirmed: "ok",
  attended: "ok",
  skipped: "plain",
  declined: "bad",
};

export const PITCH_LABEL: Record<PitchKind, string> = {
  speaker: "Speaker proposal",
  award: "Award entry",
  exhibitor: "Exhibitor enquiry",
  sponsor: "Sponsorship enquiry",
};

export const PITCH_STATUS_LABEL: Record<string, string> = {
  drafted: "Being drafted",
  awaiting_approval: "Waiting for you",
  queued: "Approved, going out",
  sent: "Sent",
  rejected: "Rejected",
  cancelled: "Withdrawn",
};

export const DEADLINE_LABEL: Record<DeadlineKind, string> = {
  speaking: "Speaker applications close",
  award: "Award entries close",
  exhibitor: "Exhibitor bookings close",
  sponsor: "Sponsorship closes",
  booking: "Booking closes",
  other: "Deadline",
};

export const SOURCE_LABEL: Record<EventRow["source"], string> = {
  register: "Source register",
  web_search: "Web search",
  inbox: "Forwarded email",
  owner: "Added by you",
  agent: "The engine",
};

function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function num(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

/** The business's events settings, with the defaults of tenants/_template/tenant.yaml. */
export function eventSettings(config: JsonObject | null | undefined): EventSettings {
  const network = (config?.network ?? {}) as JsonObject;
  const c = (network.events ?? {}) as JsonObject;
  const roles = arr(c.roles).filter((r): r is EventRole => ROLES.includes(r as EventRole));
  const markets = arr(c.markets).filter((m): m is string => typeof m === "string" && /^[A-Za-z]{2}$/.test(m)).map((m) => m.toUpperCase());
  return {
    enabled: c.enabled === true,
    keywords: arr(c.keywords).filter((k): k is string => typeof k === "string" && k.trim() !== ""),
    markets: markets.length ? markets : ["GB"],
    max_travel_km: num(c.max_travel_km, 300),
    base: typeof c.base === "string" ? c.base : "",
    horizon_months: num(c.horizon_months, 9),
    max_cost: num(c.max_cost, 0),
    roles: roles.length ? roles : ["attend", "speak"],
    follow_up_days: num(c.follow_up_days, 2),
    approve_events: c.approve_events !== false,
    approve_pitches: c.approve_pitches !== false,
  };
}

/** The reach in one line each, for the settings panel. */
export function reachLines(s: EventSettings, currency = "£"): [string, string][] {
  return [
    ["Topics", s.keywords.length ? s.keywords.join(", ") : "None set yet"],
    ["Markets", s.markets.join(", ")],
    ["Travel", s.max_travel_km === 0 ? "Online events only" : `Up to ${s.max_travel_km.toLocaleString("en-GB")} km${s.base ? ` from ${s.base}` : ""}`],
    ["Cost per event", s.max_cost === 0 ? "Free events only" : `Up to ${currency}${s.max_cost.toLocaleString("en-GB")}`],
    ["Looks ahead", `${s.horizon_months} months`],
    ["What you do there", s.roles.map((r) => ROLE_LABEL[r]).join(", ")],
    ["Follow-up", `Within ${s.follow_up_days} ${s.follow_up_days === 1 ? "day" : "days"} of the event`],
    ["Approvals", `${s.approve_events ? "You choose every event" : "The engine chooses events"}; ${s.approve_pitches ? "you approve every pitch" : "pitches go to your outreach approvals"}`],
  ];
}

export type EventGroup = "decide" | "chosen" | "confirmed" | "found" | "past" | "skipped";

export const GROUP_TITLE: Record<EventGroup, string> = {
  decide: "Shortlisted: for you to choose",
  chosen: "Chosen",
  confirmed: "Confirmed",
  found: "Found, not shortlisted",
  past: "Been and gone",
  skipped: "Skipped or declined",
};

function lastDay(e: Pick<EventRow, "starts_on" | "ends_on">): string {
  return e.ends_on ?? e.starts_on;
}

/** Which list an event belongs on today. */
export function groupOf(e: EventRow, today: string): EventGroup {
  if (e.status === "skipped" || e.status === "declined") return "skipped";
  if (e.status === "attended" || lastDay(e) < today) return "past";
  if (e.status === "shortlisted") return "decide";
  if (e.status === "chosen" || e.status === "pitched") return "chosen";
  if (e.status === "confirmed") return "confirmed";
  return "found";
}

/** Events in groups, in the order the page shows them; each group by date (shortlisted by deadline first). */
export function groupEvents(rows: EventRow[], today: string): { group: EventGroup; rows: EventRow[] }[] {
  const order: EventGroup[] = ["decide", "chosen", "confirmed", "found", "past", "skipped"];
  const by = new Map<EventGroup, EventRow[]>();
  for (const r of rows) {
    const g = groupOf(r, today);
    by.set(g, [...(by.get(g) ?? []), r]);
  }
  const key = (r: EventRow, g: EventGroup) => (g === "decide" ? r.deadline ?? r.starts_on : r.starts_on);
  return order
    .filter((g) => by.has(g))
    .map((g) => ({ group: g, rows: by.get(g)!.sort((a, b) => key(a, g).localeCompare(key(b, g)) || a.name.localeCompare(b.name)) }));
}

/** Whole days from one date (YYYY-MM-DD) to another. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** "Speaker applications close in 6 days", "… closed", or null without a deadline. */
export function deadlineText(e: Pick<EventRow, "deadline" | "deadline_kind">, today: string): { text: string; urgent: boolean } | null {
  if (!e.deadline) return null;
  const label = DEADLINE_LABEL[e.deadline_kind ?? "other"];
  const d = daysBetween(today, e.deadline);
  if (d < 0) return { text: `${label}: closed`, urgent: false };
  if (d === 0) return { text: `${label} today`, urgent: true };
  return { text: `${label} in ${d} ${d === 1 ? "day" : "days"}`, urgent: d <= 14 };
}

/** The roles a person can choose for an event: those it offers (all when it says nothing), always including attend. */
export function choicesFor(e: Pick<EventRow, "roles">): EventRole[] {
  const offered = e.roles.length ? e.roles : ROLES;
  return ROLES.filter((r) => r === "attend" || offered.includes(r));
}

/** What is wrong with a hand-added event, or null. Mirrors act's add_event checks. */
export function newEventProblem(e: NewEvent, today: string): string | null {
  if (e.name.trim().length < 2) return "Name the event.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(e.starts_on)) return "Give the first day of the event.";
  if ((e.ends_on ?? e.starts_on) < today) return "That event has already happened.";
  if (e.ends_on && e.ends_on < e.starts_on) return "The last day is before the first.";
  if (e.url && !/^https?:\/\/\S+$/.test(e.url.trim())) return "The web address should start with https://.";
  if (e.organiser_contact && !/^[^\s@<>]+@[^\s@<>]+\.[a-z]{2,}$/i.test(e.organiser_contact.trim())) return "The organiser's contact must be an email address they publish.";
  if (e.cost !== undefined && (!Number.isFinite(e.cost) || e.cost < 0)) return "The cost must be an amount from 0.";
  if (e.country && !/^[A-Za-z]{2}$/.test(e.country.trim())) return "Use a two-letter country code, like GB.";
  return null;
}

/** What is wrong with a contact typed in, or null. */
export function newContactProblem(c: NewContact): string | null {
  if (!c.full_name?.trim() && !c.email?.trim()) return "Add at least a name or an email address.";
  if (c.email?.trim() && !/^[^\s@<>]+@[^\s@<>]+\.[a-z]{2,}$/i.test(c.email.trim())) return "That email address does not look right.";
  if (c.phone?.trim() && !/^\+?[0-9 ()-]{6,30}$/.test(c.phone.trim())) return "That phone number does not look right.";
  return null;
}

/** A card photo the inbox agent can read: JPEG, PNG or WebP, at most 10 MB. */
export function cardPhotoProblem(file: Pick<File, "type" | "size" | "name">): string | null {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) && !/\.(jpe?g|png|webp)$/i.test(file.name)) return "Use a JPEG, PNG or WebP photo (on an iPhone, choose ‘Most compatible’ in camera settings).";
  if (file.size > 10 * 1024 * 1024) return "The photo is larger than 10 MB.";
  return null;
}

/**
 * 2.24 What the owner's calendar says about an event's dates, in plain words. A clash is a warning, never a block:
 * the person may still choose the event. "unknown" when not checked yet; "off" when the calendar is not connected.
 */
export function calendarNote(check: CalendarCheck | null | undefined, timeZone?: string): { tone: "free" | "clash" | "unknown" | "off"; text: string } {
  if (!check) return { tone: "unknown", text: "Your calendar has not been checked for these dates yet." };
  const when = (iso: string | undefined) => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-GB", { day: "numeric", month: "short", ...(timeZone ? { timeZone } : {}) }) : null;
  };
  const checked = when(check.checked_at);
  const on = checked ? ` (checked ${checked})` : "";
  if (check.status === "free") return { tone: "free", text: `Your calendar is free on these dates${on}.` };
  if (check.status === "not_connected") return { tone: "off", text: "Connect Google Calendar to check these dates against your diary." };
  if (check.status === "error") return { tone: "unknown", text: "Your calendar could not be checked for these dates." };
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", ...(timeZone ? { timeZone } : {}) });
  const time = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", ...(timeZone ? { timeZone } : {}) });
  const list = (check.clashes ?? []).slice(0, 3).map((c) => `${fmt(c.start)} to ${time(c.end)}`);
  const more = (check.clashes?.length ?? 0) - list.length;
  return {
    tone: "clash",
    text: `Clash: your calendar shows you busy ${list.join("; ")}${more > 0 ? ` and ${more} more` : ""}${on}. You can still choose it.`,
  };
}

/** "14–15 Jan 2027" or "14 Jan 2027". */
export function eventDates(e: Pick<EventRow, "starts_on" | "ends_on">): string {
  const fmt = (d: string, withYear = true) =>
    new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}), timeZone: "UTC" });
  if (!e.ends_on || e.ends_on === e.starts_on) return fmt(e.starts_on);
  const sameMonth = e.starts_on.slice(0, 7) === e.ends_on.slice(0, 7);
  return sameMonth ? `${Number(e.starts_on.slice(8, 10))}–${fmt(e.ends_on)}` : `${fmt(e.starts_on, false)} – ${fmt(e.ends_on)}`;
}

// ---------------------------------------------------------------------------
// 2.22 Coming up in your industry (crm_industry_calendar, migration 20270321091500)
// ---------------------------------------------------------------------------
export type IndustryKind = "event" | "award_entry_deadline" | "nomination_deadline" | "ceremony" | "speaker_deadline" | "exhibitor_deadline";

/** One dated edition of an industry source, as crm_industry_editions returns it (nulls stripped). */
export interface IndustryEdition {
  source_key: string;
  source_name: string;
  source_type: string;
  label: string;
  kind: IndustryKind;
  starts_on: string;
  ends_on?: string;
  city?: string;
  place?: string;
  country?: string;
  online?: boolean;
  cost_note?: string;
  /** The edition's page, else the source's; never there for a source flagged unsafe. */
  url?: string;
  source_url?: string;
  flags?: string[];
}

export interface IndustryCalendar {
  /** The business's niches from network.events; none means the industry is not set. */
  niches: string[];
  markets: string[];
  from: string;
  to: string;
  editions: IndustryEdition[];
}

export const INDUSTRY_KIND_LABEL: Record<IndustryKind, string> = {
  event: "Event",
  award_entry_deadline: "Award entries close",
  nomination_deadline: "Nominations close",
  ceremony: "Award ceremony",
  speaker_deadline: "Speaker applications close",
  exhibitor_deadline: "Exhibitor bookings close",
};

/** A deadline row is something to enter by that day; the rest are days to be there. */
export function isIndustryDeadline(k: IndustryKind): boolean {
  return k.endsWith("_deadline");
}

/** The link to show: the edition's own page, else its source's; none for an unsafe site. */
export function editionLink(e: IndustryEdition): string | null {
  if (e.flags?.includes("unsafe_site")) return null;
  return e.url ?? e.source_url ?? null;
}

const EDITION_DEADLINE: Partial<Record<IndustryKind, DeadlineKind>> = {
  award_entry_deadline: "award",
  nomination_deadline: "award",
  speaker_deadline: "speaking",
  exhibitor_deadline: "exhibitor",
};

/** An edition as the business's own event (act add_event, 2.22), as the engine maps it (engine/events.py
 * events_from_editions): a deadline row is added on its own day with the deadline set, so it is not missed. */
export function editionEvent(e: IndustryEdition): NewEvent {
  const deadline = EDITION_DEADLINE[e.kind];
  const link = editionLink(e);
  const place = [e.place, e.city].filter(Boolean).join(", ");
  const roles: EventRole[] = e.kind === "ceremony" || deadline === "award" ? ["attend", "award"] : e.source_type === "consumer_fair" ? ["attend", "exhibit"] : e.source_type === "trade_event" ? ["attend", "exhibit", "speak", "sponsor"] : ["attend"];
  return {
    name: e.label,
    starts_on: e.starts_on,
    ...(e.ends_on ? { ends_on: e.ends_on } : {}),
    ...(link ? { url: link } : {}),
    ...(place ? { place } : {}),
    ...(e.country ? { country: e.country } : {}),
    ...(e.online ? { online: true } : {}),
    organiser: e.source_name,
    ...(deadline ? { deadline: e.starts_on, deadline_kind: deadline } : {}),
    roles,
  };
}
